import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchMyPosts, deleteUserPost } from '../../services/api';
import { fetchUserProfile } from '../../services/api';
import Sidebar from './Sidebar';
import { FaEllipsisV } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button } from 'react-bootstrap';

const emptyStateText = {
  draft: {
    title: 'Nothing cooking yet',
    body: 'Start a draft and this space will fill with your soon-to-be hilarious ideas.',
  },
  submitted: {
    title: 'Waiting in the wings',
    body: 'Your submitted stories will show up here after you hit send.',
  },
  denied: {
    title: 'All approved… for now',
    body: 'None of your stories have been denied yet — keep up the good work.',
  },
  approved: {
    title: 'Awaiting glory',
    body: 'Approved stories will appear here once they earn their spot.',
  },
};


const DEFAULT_FEATURED_IMAGE =
    'https://res.cloudinary.com/dnbbm9vzi/image/upload/v1736181865/web_development___website_webpage_browser_ad_advertisement_man_people_ibpgaq.png';

const getCloudinaryUrl = (img) => {
    if (!img) return DEFAULT_FEATURED_IMAGE;
    // If img already contains 'image/upload/', just prepend the base Cloudinary URL
    if (img.includes('image/upload/')) {
        return `https://res.cloudinary.com/dbm8xbouw/${img}`;
    }
    // Otherwise, assume it's just an ID and prepend the standard path
    return `https://res.cloudinary.com/dbm8xbouw/image/upload/${img}`;
};


const StoriesPage = () => {
    const token = localStorage.getItem('authToken');
    const [posts, setPosts] = useState([]);
    const [tab, setTab] = useState('draft'); // draft, submitted, denied, approved
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [menuOpen, setMenuOpen] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        () => JSON.parse(localStorage.getItem('sidebarCollapsed')) || false
    );

    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
    }, [sidebarCollapsed]);

    const [showReviewNoteModal, setShowReviewNoteModal] = useState(false);
    const [currentReviewNote, setCurrentReviewNote] = useState('');

    const [showReadOnlyModal, setShowReadOnlyModal] = useState(false);
    const [currentArticle, setCurrentArticle] = useState(null);

    const handleReadOnlyView = (post) => {
    setCurrentArticle(post);
    setShowReadOnlyModal(true);
    };



    // <-- Add the user profile state & fetch here
    const [userProfile, setUserProfile] = useState(null);

    useEffect(() => {
        const loadProfile = async () => {
            if (!token) return;
            try {
                const profileData = await fetchUserProfile(token);
                setUserProfile(profileData);
            } catch (err) {
                console.error('Error fetching user profile:', err);
            }
        };
        loadProfile();
    }, [token]);



    const navigate = useNavigate();

    useEffect(() => {
        const loadPosts = async () => {
            try {
                const data = await fetchMyPosts(token);
                setPosts(data);
            } catch (err) {
                console.error(err);
                setError('Failed to load stories.');
            } finally {
                setLoading(false);
            }
        };
        loadPosts();
    }, [token]);

    const tabStatusMap = { draft: 0, submitted: 1, denied: 3, approved: 2 };
    const tabCounts = posts.reduce((acc, post) => {
        const key = Object.keys(tabStatusMap).find(
            k => tabStatusMap[k] === post.status
        );
        if (key) acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    const filteredPosts = posts.filter(post => post.status === tabStatusMap[tab]);

    const handlePublicView = (post) => {
    if (!post.slug) return;
    window.open(`/user-posts/${post.slug}`, '_blank'); // <-- points to new route
    };


    const handleViewReviewNote = (note) => {
        setCurrentReviewNote(note || 'No review note available.');
        setShowReviewNoteModal(true);
    };


    const openDeleteModal = (post) => {
        setSelectedPost(post);
        setShowDeleteModal(true);
        setMenuOpen(null);
    };

    const handleConfirmDelete = async () => {
        if (!selectedPost) return;
        try {
            await deleteUserPost(selectedPost.slug, token);
            setPosts(prev => prev.filter(post => post.slug !== selectedPost.slug));
        } catch (err) {
            console.error('Error deleting story:', err);
            alert('Failed to delete story.');
        } finally {
            setShowDeleteModal(false);
            setSelectedPost(null);
        }
    };

    return (
        <div className="dashboard-background">
            <div className="container-fluid">
                {error && <div style={{ color: 'red' }}>{error}</div>}
                <div className="d-flex">
                    <Sidebar userProfile={userProfile}
                        isCollapsed={sidebarCollapsed}
                        setIsCollapsed={setSidebarCollapsed}
                    />
                    <main className="flex-grow-1 px-4" 
                        style={{
                            minHeight: '80vh',
                            marginLeft: sidebarCollapsed ? '60px' : '220px'
                        }}>
                        <div className="text-center mb-4">
                            <img
                                src="https://res.cloudinary.com/dnbbm9vzi/image/upload/v1736174973/image-removebg-preview_1_rez4gn.png"
                                alt="Logo"
                                style={{ width: '100px', height: '100px' }}
                            />
                            <h1 className="fw-bold d-inline mb-0">My Stories</h1>
                        </div>

                        <div className="d-flex align-items-center justify-content-between mb-4">
                            {/* Segmented tab switcher */}
                            <div className="btn-group w-100" role="group" aria-label="Story status tabs" style={{ maxWidth: '450px' }}>
                                {['draft', 'submitted', 'denied', 'approved'].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={`btn ${tab === t ? 'btn-dark' : 'btn-outline-dark'}`}
                                        style={{ flex: 1 }}
                                        onClick={() => setTab(t)}
                                    >
                                        <span className="d-flex align-items-center justify-content-center gap-2 fw-bold">
                                            {t.charAt(0).toUpperCase() + t.slice(1)}
                                            {tabCounts[t] > 0 && (
                                                <span className="badge bg-light text-dark">
                                                    {tabCounts[t]}
                                                </span>
                                            )}
                                        </span>

                                    </button>
                                ))}
                            </div>

                            {/* Create Story */}
                            <button
                                className="btn btn-dark fw-bold"
                                onClick={() => navigate('/create-story')}
                            >
                                + Create Story
                            </button>
                        </div>


                        <div className="container mt-3">
                            <div className="d-flex justify-content-center">
                                <div className="list-group mx-auto" style={{ maxWidth: '1100px', width: '100%' }}>
                                    {loading ? (
                                        <p>Loading stories...</p>
                                    ) : filteredPosts.length === 0 ? (
                                        <div className="row">
                                                <div className="col-md-6 d-flex flex-column align-items-center justify-content-center">
                                                    <h1 className="text-center mb-3 fw-bold">
                                                        {emptyStateText[tab]?.title || `No ${tab} stories yet.`}
                                                    </h1>
                                                    <h5 className="text-center mb-3">
                                                        {emptyStateText[tab]?.body || `No ${tab} stories.`}
                                                    </h5>

                                                    <button
                                                        className="btn btn-dark fw-bold mt-2"
                                                        onClick={() => navigate('/create-story')}
                                                    >
                                                        + Create Story
                                                    </button>
                                            </div>
                                            <div className="col-md-6">
                                                <img
                                                    src="https://res.cloudinary.com/dnbbm9vzi/image/upload/v1736181865/web_development___website_webpage_browser_ad_advertisement_man_people_ibpgaq.png"
                                                    alt="No stories"
                                                    className="img-fluid"
                                                    style={{ maxWidth: '100%', height: 'auto' }}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        filteredPosts.map((post) => {
                                            const isDraft = post.status === 0;
                                            const isApproved = post.status === 2;
                                            const isReadOnly = !isDraft;

                                            const isInteractive = post.status === tabStatusMap['draft']
                                                || post.status === tabStatusMap['submitted']
                                                || post.status === tabStatusMap['denied'];

                                            return (
                                                <div
                                                    key={post.id}
                                                    className="list-group-item d-flex align-items-start mb-4 justify-content-between"
                                                    style={{
                                                        transition: 'background-color 0.3s ease',
                                                        width: '100%',
                                                        cursor: isInteractive ? 'pointer' : 'default',
                                                    }}
                                                    onMouseEnter={(e) => isInteractive && (e.currentTarget.style.backgroundColor = '#E5E7EB')}
                                                    onMouseLeave={(e) => isInteractive && (e.currentTarget.style.backgroundColor = '')}
                                                    onClick={() => {
                                                        if (post.status === tabStatusMap['draft']) {
                                                            navigate(`/story-editor/${post.slug}`);
                                                        } else if (post.status === tabStatusMap['submitted'] || post.status === tabStatusMap['denied']) {
                                                            handleReadOnlyView(post);
                                                        }
                                                    }}
                                                >
                                                    <img
                                                        src={getCloudinaryUrl(post.featured_image)}
                                                        alt={post.title}
                                                        onError={(e) => { e.currentTarget.src = DEFAULT_FEATURED_IMAGE; }}
                                                        className="img-thumbnail me-3"
                                                        style={{ width: '100px', height: 'auto', objectFit: 'cover' }}
                                                    />



                                                    <div className="flex-grow-1">
                                                        <h5 className="mb-1 fw-bold">{post.title}</h5>
                                                        <p className="mb-1 text-muted">{post.excerpt || 'No description available'}</p>
                                                    </div>

                                                    <div className="d-flex flex-row ms-3 position-relative gap-4">
                                                        {(post.review_note && (tab === 'denied' || tab === 'approved')) && (
                                                            <button
                                                                className="btn btn-sm btn-outline-primary mb-2 fw-bold"
                                                                style={{ minWidth: '120px' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation(); // ← Prevent triggering parent div's onClick
                                                                    handleViewReviewNote(post.review_note);
                                                                }}
                                                            >
                                                                View Review Note
                                                            </button>
                                                        )}

                                                        {isApproved && (
                                                            <button
                                                                className="btn btn-sm btn-outline-dark mb-2 fw-bold"
                                                                style={{ minWidth: '120px' }}
                                                                onClick={() => handlePublicView(post)}
                                                            >
                                                                Public View
                                                            </button>
                                                        )}

                                                        <div className="dropdown">
                                                            <button
                                                                className="btn btn-sm btn-outline-dark"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();  // ← Prevent row click
                                                                    setMenuOpen(menuOpen === post.id ? null : post.id);
                                                                }}
                                                            >
                                                                <FaEllipsisV />
                                                            </button>
                                                            {menuOpen === post.id && (
                                                                <div
                                                                    className="dropdown-menu show"
                                                                    style={{ position: 'absolute', right: 0 }}
                                                                >
                                                                    <button
                                                                        className="dropdown-item text-danger"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();  // ← Prevent row click
                                                                            openDeleteModal(post);
                                                                        }}
                                                                    >
                                                                        Delete Story
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                    </div>
                                                </div>

                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Bootstrap Delete Confirmation Modal */}
                        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                            <Modal.Header className="justify-content-center position-relative">
                                <Modal.Title className="fw-bold text-center">
                                    Confirm Delete
                                </Modal.Title>
                            </Modal.Header>

                            <Modal.Body className="text-center">
                                Are you sure you want to permanently delete this story?
                            </Modal.Body>

                            <Modal.Footer className="d-flex justify-content-between">
                                <Button
                                    variant="dark"
                                    className="fw-bold"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    variant="danger"
                                    className="fw-bold"
                                    onClick={handleConfirmDelete}
                                >
                                    Delete
                                </Button>
                            </Modal.Footer>
                        </Modal>


                        {/* Review Note Modal */}
                        <Modal
                            show={showReviewNoteModal}
                            onHide={() => setShowReviewNoteModal(false)}
                            centered
                        >
                            <Modal.Header closeButton className="position-relative">
                                {/* Left: role badge */}
                                <span
                                    className="position-absolute start-0 ms-3 top-50 translate-middle-y badge bg-warning text-dark"
                                    style={{
                                        fontSize: '0.95rem',      // larger font
                                        fontWeight: 600,           // semi-bold
                                        padding: '6px 12px',       // bigger pill
                                        borderRadius: '12px',      // rounded pill shape
                                    }}
                                >
                                    Editor
                                </span>
                                <Modal.Title className="fw-bold position-absolute start-50 translate-middle-x">
                                    Jessster Review Note
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body style={{ whiteSpace: 'pre-wrap' }}>
                                <p>{currentReviewNote}</p>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="dark" className="fw-bold" onClick={() => setShowReviewNoteModal(false)}>
                                    Close
                                </Button>
                            </Modal.Footer>
                        </Modal>
                        {/* Review submitted or denied Article */}
                        <Modal show={showReadOnlyModal} onHide={() => setShowReadOnlyModal(false)} size="lg" centered scrollable>
                            <Modal.Header closeButton>
                                <Modal.Title className="fw-bold">{currentArticle?.title}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                {/* Excerpt */}
                                {currentArticle?.excerpt && (
                                    <p className="text-muted fst-italic mb-3">
                                        {currentArticle.excerpt}
                                    </p>
                                )}
                                <hr />
                                {/* Article content */}
                                <div dangerouslySetInnerHTML={{ __html: currentArticle?.content }} />
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="dark" className="fw-bold" onClick={() => setShowReadOnlyModal(false)}>Close</Button>
                            </Modal.Footer>
                        </Modal>

                    </main>
                </div>
            </div>
        </div>
    );
};

export default StoriesPage;
